import {BaseClient} from 'common/report/queries/utils/base-client';
import {assertUnreachable} from 'common/util/typescript';
import {ProfileQueryEndpoint, ProfileQueryParameters} from 'common/report/util/profile/types';

import {queryApiUrl} from '../utils';

class ProfileClient extends BaseClient {
  public buildEndpoint({endpoint}: {endpoint: ProfileQueryEndpoint}): string {
    switch (endpoint) {
      case ProfileQueryEndpoint.ActivityStream:
        return queryApiUrl(`stream/query`);
      case ProfileQueryEndpoint.Aliases:
      case ProfileQueryEndpoint.Messages:
        return queryApiUrl(`engage/${endpoint}`);
      case ProfileQueryEndpoint.Engage:
        return queryApiUrl(`engage/`);
      default:
        return assertUnreachable(endpoint);
    }
  }

  public buildQueryParams(params: ProfileQueryParameters): {[key: string]: any} {
    const {dataGroupID, projectId, workspaceId, distinctID, toDate, fromDate, distinctIDs, limit} = params;
    return {
      project_id: projectId,
      ...(dataGroupID ? {data_group_id: dataGroupID} : {}),
      ...(workspaceId ? {workspace_id: workspaceId} : {}),
      ...(distinctID ? {distinct_id: distinctID} : {}),
      ...(toDate ? {to_date: toDate} : {}),
      ...(fromDate ? {from_date: fromDate} : {}),
      // Must stringify this array now to prevent the 'query-string' package from changing it to "distinct_ids=X" as query-api is expecting an array
      ...(distinctIDs ? {distinct_ids: JSON.stringify(distinctIDs)} : {}),
      ...(limit ? {limit} : {}),
    };
  }

  public buildRequestBody(params: ProfileQueryParameters): string {
    const {excludeEvents, includeEvents} = params;

    if (excludeEvents?.length > 0 && includeEvents?.length > 0) {
      throw new Error(`Cannot request stream/query with both included and excluded events`);
    } else if (excludeEvents?.length > 0) {
      return JSON.stringify({
        exclude_events: JSON.stringify(excludeEvents),
      });
    } else if (includeEvents?.length > 0) {
      return JSON.stringify({
        include_events: JSON.stringify(includeEvents),
      });
    }

    return null;
  }

  public processResponse(response): Obj {
    return response;
  }

  public async fetchProfileEndpoint<ResultsT>(
    params: ProfileQueryParameters,
    endpoint: ProfileQueryEndpoint,
    clearCache = false,
  ): Promise<ResultsT> {
    const cacheParams = {...params, endpoint};
    const cached = !!this.getCachedResponse(cacheParams);
    let results = {};
    await this.query(cacheParams, {clearCache})
      .then((response) => {
        results = {
          error: null,
          results: response,
          cached,
        };
      })
      .catch((error) => {
        results = {cached, error, results: null};
      });
    return results as ResultsT;
  }
}

export const profileClient = new ProfileClient();
